import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Gavel,
  Plus,
  Trash2,
  User,
  ChevronDown,
  ChevronUp,
  Edit2,
  Save,
  X,
} from "lucide-react";
import {
  decisionTypeColors,
  decisionStatusColors,
} from "../../lib/decisions";

// ─── Single Decision Block ────────────────────────────────────────────────────

const DecisionBlock = ({ decision, participants, isLocked, onUpdate, onDelete }) => {
  const { t, i18n } = useTranslation("meeting");

  const decisionTypeLabels = useMemo(
    () => ({
      strategy:   t("decType_strategy"),
      finance:    t("decType_finance"),
      governance: t("decType_governance"),
      operations: t("decType_operations"),
      personnel:  t("decType_personnel"),
      compliance: t("decType_compliance"),
    }),
    [t]
  );

  const expectedOutcomeLabels = useMemo(
    () => ({
      decision:    t("expOutcome_decision"),
      resolution:  t("expOutcome_resolution"),
      approval:    t("expOutcome_approval"),
      discussion:  t("expOutcome_discussion"),
      information: t("expOutcome_information"),
    }),
    [t]
  );

  const decisionStatusLabels = useMemo(
    () => ({
      pending:  t("decStatus_pending"),
      approved: t("decStatus_approved"),
      rejected: t("decStatus_rejected"),
      deferred: t("decStatus_deferred"),
      noted:    t("decStatus_noted"),
    }),
    [t]
  );
  const [isExpanded, setIsExpanded]     = useState(true);
  const [isEditing, setIsEditing]       = useState(!decision.decisionText);
  const [edited, setEdited]             = useState(decision);

  const handleSave = () => {
    onUpdate(edited);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEdited(decision);
    setIsEditing(false);
  };

  const handleVote = (participantId, participantName, vote) => {
    const idx = edited.votes.findIndex((v) => v.participantId === participantId);
    const newVote = { participantId, participantName, vote, votedAt: new Date().toISOString() };
    if (idx >= 0) {
      const updated = [...edited.votes];
      updated[idx] = newVote;
      setEdited({ ...edited, votes: updated });
    } else {
      setEdited({ ...edited, votes: [...edited.votes, newVote] });
    }
  };

  const voteSummary = {
    for:     edited.votes.filter((v) => v.vote === "for").length,
    against: edited.votes.filter((v) => v.vote === "against").length,
    abstain: edited.votes.filter((v) => v.vote === "abstain").length,
  };

  return (
    <Card className="border-l-4 border-l-primary bg-muted/10">
      <CardContent className="p-0">
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/20"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Gavel className="w-5 h-5 text-primary shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-foreground">{decision.title}</h4>
                <Badge className={decisionTypeColors[decision.decisionType] || ""}>
                  {decisionTypeLabels[decision.decisionType] || decision.decisionType}
                </Badge>
                <Badge variant="outline" className={decisionStatusColors[decision.status] || ""}>
                  {decisionStatusLabels[decision.status] || decision.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {expectedOutcomeLabels[decision.expectedOutcome] || decision.expectedOutcome}
                {decision.decisionOwner && t("decisionOwnerSuffix", { name: decision.decisionOwner })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-2">
            {(voteSummary.for + voteSummary.against + voteSummary.abstain) > 0 && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-green-600">{t("voteSummaryFor", { count: voteSummary.for })}</span>
                <span className="text-red-600">{t("voteSummaryAgainst", { count: voteSummary.against })}</span>
                <span className="text-slate-500">{t("voteSummaryAbstain", { count: voteSummary.abstain })}</span>
              </div>
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-border/50">
            {/* Decision text */}
            <div className="pt-4">
              <Label className="text-xs text-muted-foreground mb-2 block">{t("decisionText")}</Label>
              {isEditing && !isLocked ? (
                <Textarea
                  value={edited.decisionText}
                  onChange={(e) => setEdited({ ...edited, decisionText: e.target.value })}
                  placeholder={t("recordDecisionPh")}
                  rows={3}
                  className="text-sm"
                />
              ) : (
                <div className="p-3 bg-muted/30 rounded-md border">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {decision.decisionText || (
                      <span className="text-muted-foreground italic">{t("noDecisionRecorded")}</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Vote record */}
            {participants.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">{t("voteRecord")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {participants.map((p) => {
                    const existing = edited.votes.find((v) => v.participantId === p.id);
                    return (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-muted/20 rounded-md border">
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm truncate max-w-[80px]">{p.name}</span>
                        </div>
                        {isEditing && !isLocked ? (
                          <div className="flex items-center gap-1">
                            {["for", "against", "abstain"].map((vote) => (
                              <Button
                                key={vote}
                                variant={
                                  existing?.vote === vote
                                    ? vote === "for" ? "default" : vote === "against" ? "destructive" : "secondary"
                                    : "outline"
                                }
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={(e) => { e.stopPropagation(); handleVote(p.id, p.name, vote); }}
                              >
                                {vote === "for" ? t("vote_for") : vote === "against" ? t("vote_against") : t("vote_abstain")}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <Badge
                            variant="outline"
                            className={
                              existing?.vote === "for"      ? "text-green-600 border-green-300" :
                              existing?.vote === "against"  ? "text-red-600 border-red-300" :
                              existing?.vote === "abstain"  ? "text-slate-600 border-slate-300" :
                              "text-muted-foreground"
                            }
                          >
                            {existing?.vote === "for"
                              ? t("vote_for")
                              : existing?.vote === "against"
                                ? t("vote_against")
                                : existing?.vote === "abstain"
                                  ? t("vote_abstain")
                                  : "—"}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Owner + Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">{t("decisionOwnerField")}</Label>
                {isEditing && !isLocked ? (
                  <Select
                    value={edited.decisionOwner}
                    onValueChange={(v) => setEdited({ ...edited, decisionOwner: v })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t("selectOwner")} />
                    </SelectTrigger>
                    <SelectContent>
                      {participants.map((p) => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-2 bg-muted/20 rounded-md border text-sm">
                    {decision.decisionOwner || "—"}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">{t("statusLabel")}</Label>
                {isEditing && !isLocked ? (
                  <Select
                    value={edited.status}
                    onValueChange={(v) => setEdited({ ...edited, status: v })}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(decisionStatusLabels).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={decisionStatusColors[decision.status] || ""}>
                    {decisionStatusLabels[decision.status] || decision.status}
                  </Badge>
                )}
              </div>
            </div>

            {/* Follow-up */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                {isEditing && !isLocked && (
                  <Checkbox
                    checked={edited.followUpRequired}
                    onCheckedChange={(c) => setEdited({ ...edited, followUpRequired: !!c })}
                  />
                )}
                <Label className="text-xs text-muted-foreground">{t("followUpRequiredLabel")}</Label>
              </div>
              {(edited.followUpRequired || decision.followUpRequired) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    {isEditing && !isLocked ? (
                      <Textarea
                        value={edited.followUpDescription || ""}
                        onChange={(e) => setEdited({ ...edited, followUpDescription: e.target.value })}
                        placeholder={t("followUpPh")}
                        rows={2}
                        className="text-sm"
                      />
                    ) : (
                      <div className="p-2 bg-muted/20 rounded-md border text-sm">
                        {decision.followUpDescription || "—"}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">{t("reviewDate")}</Label>
                    {isEditing && !isLocked ? (
                      <Input
                        type="date"
                        value={edited.reviewDate || ""}
                        onChange={(e) => setEdited({ ...edited, reviewDate: e.target.value })}
                        className="h-9"
                      />
                    ) : (
                      <div className="p-2 bg-muted/20 rounded-md border text-sm">
                        {decision.reviewDate
                          ? new Date(decision.reviewDate).toLocaleDateString(i18n.resolvedLanguage || "en")
                          : "—"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            {!isLocked && (
              <div className="flex items-center justify-between pt-2 border-t border-border/50">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={handleSave} className="gap-1">
                      <Save className="w-3 h-3" /> {t("save")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancel} className="gap-1">
                      <X className="w-3 h-3" /> {t("cancel")}
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} className="gap-1">
                    <Edit2 className="w-3 h-3" /> {t("edit")}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={onDelete} className="text-destructive gap-1">
                  <Trash2 className="w-3 h-3" /> {t("remove")}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── DecisionBlocksSection ────────────────────────────────────────────────────

export const DecisionBlocksSection = ({ decisions = [], participants = [], isLocked, onDecisionsChange }) => {
  const { t } = useTranslation("meeting");

  const decisionTypeLabels = useMemo(
    () => ({
      strategy:   t("decType_strategy"),
      finance:    t("decType_finance"),
      governance: t("decType_governance"),
      operations: t("decType_operations"),
      personnel:  t("decType_personnel"),
      compliance: t("decType_compliance"),
    }),
    [t]
  );

  const expectedOutcomeLabels = useMemo(
    () => ({
      decision:    t("expOutcome_decision"),
      resolution:  t("expOutcome_resolution"),
      approval:    t("expOutcome_approval"),
      discussion:  t("expOutcome_discussion"),
      information: t("expOutcome_information"),
    }),
    [t]
  );

  const [isAdding, setIsAdding] = useState(false);
  const [newDec, setNewDec]     = useState({
    title:           "",
    decisionType:    "strategy",
    expectedOutcome: "decision",
  });

  const handleAdd = () => {
    if (!newDec.title.trim()) return;
    const decision = {
      id:                  `dec-${Date.now()}`,
      title:               newDec.title,
      decisionType:        newDec.decisionType,
      expectedOutcome:     newDec.expectedOutcome,
      decisionText:        "",
      votes:               [],
      decisionOwner:       "",
      followUpRequired:    false,
      stakeholdersAffected:[],
      status:              "pending",
    };
    onDecisionsChange([...decisions, decision]);
    setNewDec({ title: "", decisionType: "strategy", expectedOutcome: "decision" });
    setIsAdding(false);
  };

  const handleUpdate = (idx, updated) => {
    const arr = [...decisions];
    arr[idx] = updated;
    onDecisionsChange(arr);
  };

  const handleDelete = (idx) => {
    onDecisionsChange(decisions.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gavel className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">{t("decisionsSectionHeading")}</h3>
          <Badge variant="secondary">{decisions.length}</Badge>
        </div>
        {!isLocked && (
          <Dialog open={isAdding} onOpenChange={setIsAdding}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1">
                <Plus className="w-4 h-4" /> {t("addDecisionButton")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("addBoardDecisionTitle")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>{t("decisionTitleLabel")}</Label>
                  <Input
                    value={newDec.title}
                    onChange={(e) => setNewDec((p) => ({ ...p, title: e.target.value }))}
                    placeholder={t("decisionTitleExamplePh")}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("decisionType")}</Label>
                    <Select value={newDec.decisionType} onValueChange={(v) => setNewDec((p) => ({ ...p, decisionType: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(decisionTypeLabels).map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("expectedOutcome")}</Label>
                    <Select value={newDec.expectedOutcome} onValueChange={(v) => setNewDec((p) => ({ ...p, expectedOutcome: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(expectedOutcomeLabels).map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsAdding(false)}>{t("cancel")}</Button>
                  <Button onClick={handleAdd}>{t("addDecisionButton")}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {decisions.length > 0 ? (
        <div className="space-y-3">
          {decisions.map((dec, idx) => (
            <DecisionBlock
              key={dec.id}
              decision={dec}
              participants={participants}
              isLocked={isLocked}
              onUpdate={(updated) => handleUpdate(idx, updated)}
              onDelete={() => handleDelete(idx)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border border-dashed border-border rounded-lg">
          <Gavel className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">{t("noDecisionsInMeeting")}</p>
          {!isLocked && (
            <Button variant="link" size="sm" onClick={() => setIsAdding(true)} className="mt-2">
              {t("addDecisionLink")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DecisionBlock;
